import { WbButton } from '@agilelab/plugin-wb-platform';
import { FC } from 'react';
import { Box, Typography, makeStyles, useTheme } from '@material-ui/core';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import { useTeamRolesPanelContext } from '../../../context';
import { useOnboardProject } from '../../../hooks';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(3, 4),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: theme.palette.bkg.info,
    cursor: 'pointer',
    boxShadow: theme.shadows[1],
    transition: 'box-shadow 0.3s ease, transform 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-3px)',
    },
    '&:hover $iconContainer': {
      transform: 'translateY(-4px)',
    },
    '&:hover $iconContainer::after': {
      opacity: 1,
    },
  },
  iconContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 100,
    height: 100,
    minWidth: 100,
    borderRadius: '50%',
    backgroundColor: theme.palette.white,
    border: `2px dashed ${theme.palette.info.main}`,
    transition: 'transform 0.3s ease',
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      bottom: -18,
      left: '-5%',
      width: '110%',
      height: 16,
      borderRadius: '50%',
      background:
        'radial-gradient(ellipse, rgba(0, 0, 0, 0.12) 0%, transparent 70%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
  },
  icon: {
    fontSize: 60,
    color: theme.palette.info.main,
    transition: 'transform 0.3s ease',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  button: {
    whiteSpace: 'nowrap',
    borderColor: theme.palette.info.main,
    color: theme.palette.info.main,
    backgroundColor: theme.palette.white,
    fontSize: theme.typography.h4.fontSize,
  },
}));

export const OnboardBanner: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { isCatalogInfoOwner } = useTeamRolesPanelContext();
  const { loading, handleOnboardProject } = useOnboardProject();

  return (
    <Box className={classes.root}>
      <Box className={classes.iconContainer}>
        <OpenInBrowserIcon className={classes.icon} />
      </Box>
      <Box className={classes.textContainer}>
        <Typography style={{ fontWeight: 700 }} variant="h6">
          Team Roles has been enabled on the Witboost Platform
        </Typography>
        <Typography variant="h6" color="textSecondary">
          {isCatalogInfoOwner
            ? 'Onboard your project to start using Team Roles management and have more control over your team accountabilities.'
            : 'Only the project owner can onboard this project.'}
        </Typography>
      </Box>
      {isCatalogInfoOwner && (
        <WbButton
          startIcon={
            <OpenInBrowserIcon
              style={{ fontSize: theme.typography.h2.fontSize }}
            />
          }
          variant="outlined"
          label="Onboard Project"
          className={classes.button}
          loading={loading}
          onClick={handleOnboardProject}
        />
      )}
    </Box>
  );
};
